import { useEffect, useState, useMemo } from "react";
import { esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";
import { causasFalla } from "./causas";

const VACIO_LABEL = "(Sin causa / En blanco)";

function ResumenICR({ usuario }) {
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);

  const [data, setData] = useState([]);
  const [filtroMes, setFiltroMes]             = useState([]);
  const [filtroPrioridad, setFiltroPrioridad] = useState([]);
  const [filtroCausa, setFiltroCausa]         = useState([]);
  const [filtroAliado, setFiltroAliado]       = useState([]);
  const [filtroCiudad, setFiltroCiudad]       = useState([]);
  const [filtroJefe, setFiltroJefe]           = useState([]);

  // ✅ FORMATEAR FECHA
  const formatearMes = (fecha) => {
    if (!fecha) return "";
    if (fecha.includes("T")) {
      const [anio, mes] = fecha.split("T")[0].split("-");
      return `${mes}-${anio}`;
    }
    if (fecha.includes("-") && fecha.length === 10) {
      const [anio, mes] = fecha.split("-");
      return `${mes}-${anio}`;
    }
    return fecha;
  };

  // 🔄 CARGA DATOS
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/resumen-icr?rol=${encodeURIComponent(usuario?.rol||"")}&cedula=${encodeURIComponent(usuario?.cedula||"")}`)
      .then(res => res.json())
      .then(data => setData(data))
      .catch(err => console.error(err));
  }, [usuario]);

  // ✅ Opciones de mes — ordenadas del más reciente al más antiguo
  const opcionesMes = useMemo(() =>
    [...new Set(data.map(i => formatearMes(i.m_fecha_mes)))]
      .filter(Boolean)
      .sort((a, b) => {
        const [mesA, anioA] = a.split("-");
        const [mesB, anioB] = b.split("-");
        return new Date(anioB, mesB - 1) - new Date(anioA, mesA - 1);
      })
  , [data]);

  // ── OPCIONES BIDIRECCIONALES ─────────────────────────────────────────────
  // Cada filtro muestra solo las opciones que existen considerando TODOS los
  // demás filtros activos (excepto el suyo propio).

  const opcionesAliado = useMemo(() => {
    const base = data.filter(i => {
      const mes  = formatearMes(i.m_fecha_mes);
      const ciu  = (i.ciudad      || "").trim();
      const jefe = (i.nombre_jefe || "").trim();
      const prio = (i.tipo_prioridad || "").trim();
      return (
        (filtroMes.length       === 0 || filtroMes.includes(mes)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciu)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(i => (i.aliado || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroCiudad, filtroJefe, filtroPrioridad]);

  const opcionesCiudad = useMemo(() => {
    const base = data.filter(i => {
      const mes    = formatearMes(i.m_fecha_mes);
      const aliado = (i.aliado      || "").trim();
      const jefe   = (i.nombre_jefe || "").trim();
      const prio   = (i.tipo_prioridad || "").trim();
      return (
        (filtroMes.length       === 0 || filtroMes.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(i => (i.ciudad || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroAliado, filtroJefe, filtroPrioridad]);

  const opcionesJefe = useMemo(() => {
    const base = data.filter(i => {
      const mes    = formatearMes(i.m_fecha_mes);
      const aliado = (i.aliado  || "").trim();
      const ciu    = (i.ciudad  || "").trim();
      const prio   = (i.tipo_prioridad || "").trim();
      return (
        (filtroMes.length       === 0 || filtroMes.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciu)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(i => (i.nombre_jefe || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroAliado, filtroCiudad, filtroPrioridad]);

  const opcionesPrioridad = useMemo(() => {
    const base = data.filter(i => {
      const mes    = formatearMes(i.m_fecha_mes);
      const aliado = (i.aliado      || "").trim();
      const ciu    = (i.ciudad      || "").trim();
      const jefe   = (i.nombre_jefe || "").trim();
      return (
        (filtroMes.length    === 0 || filtroMes.includes(mes)) &&
        (filtroAliado.length === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0 || filtroCiudad.includes(ciu)) &&
        (filtroJefe.length   === 0 || filtroJefe.includes(jefe))
      );
    });
    return [...new Set(base.map(i => (i.tipo_prioridad || "").trim()))].filter(Boolean).sort();
  }, [data, filtroMes, filtroAliado, filtroCiudad, filtroJefe]);

  // ✅ Causa falla — usa lista oficial de causas.js (sin duplicados ni errores)
  const opcionesCausa = useMemo(() => {
    const hayVacios = data.some(i => !i.causa_falla || i.causa_falla.trim() === "");
    return hayVacios ? [VACIO_LABEL, ...causasFalla] : causasFalla;
  }, [data]);

  // ✅ Manejar selección múltiple en select
  const handleMultiSelect = (e, setFiltro) => {
    const seleccionados = Array.from(e.target.selectedOptions).map(o => o.value);
    setFiltro(seleccionados);
  };

  // ✅ DATOS FILTRADOS
  const datosFiltrados = useMemo(() => {
    return data.filter(item => {
      const mes       = formatearMes(item.m_fecha_mes);
      const aliado    = (item.aliado       || "").trim();
      const ciudad    = (item.ciudad       || "").trim();
      const jefe      = (item.nombre_jefe  || "").trim();
      const prioridad = (item.tipo_prioridad || "").trim();
      const causaReal = (item.causa_falla  || "").trim();
      const causaFiltro = causaReal === "" ? VACIO_LABEL : causaReal;
      return (
        (filtroMes.length       === 0 || filtroMes.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prioridad)) &&
        (filtroCausa.length     === 0 || filtroCausa.includes(causaFiltro))
      );
    });
  }, [data, filtroMes, filtroAliado, filtroCiudad, filtroJefe, filtroPrioridad, filtroCausa]);

  // ─────────────────────────────────────────
  // TABLA 1: CAT + Agrupación + Cantidad + % + Resumen ICR
  // ─────────────────────────────────────────
  const tabla1 = useMemo(() => {
    const totalGeneral = datosFiltrados.length;
    const porCat = {};
    datosFiltrados.forEach(item => {
      const cat = item.categoria || "Sin Cat";
      const agr = item.agrupacion || "Sin Agrupación";
      if (!porCat[cat]) porCat[cat] = {};
      if (!porCat[cat][agr]) porCat[cat][agr] = 0;
      porCat[cat][agr]++;
    });
    return { porCat, totalGeneral };
  }, [datosFiltrados]);

  // ─────────────────────────────────────────
  // TABLA 2: CAT + Reincidencia
  // ─────────────────────────────────────────
  const tabla2 = useMemo(() => {
    const reincidencias = [...new Set(datosFiltrados.map(i => i.recurrencia || "Sin dato"))].sort();
    const porCat = {};
    const totales = {};
    datosFiltrados.forEach(item => {
      const cat  = item.categoria || "Sin Cat";
      const recu = item.recurrencia || "Sin dato";
      if (!porCat[cat]) porCat[cat] = {};
      if (!porCat[cat][recu]) porCat[cat][recu] = 0;
      porCat[cat][recu]++;
      if (!totales[recu]) totales[recu] = 0;
      totales[recu]++;
    });
    return { porCat, reincidencias, totales, total: datosFiltrados.length };
  }, [datosFiltrados]);

  // ─────────────────────────────────────────
  // TABLA 3: Jefe + CAT + Reincidencia
  // ─────────────────────────────────────────
  const tabla3 = useMemo(() => {
    const reincidencias = [...new Set(datosFiltrados.map(i => i.recurrencia || "Sin dato"))].sort();
    const porJefe = {};
    datosFiltrados.forEach(item => {
      const jefe = item.nombre_jefe || "Sin Jefe";
      const cat  = item.categoria   || "Sin Cat";
      const recu = item.recurrencia || "Sin dato";
      if (!porJefe[jefe]) porJefe[jefe] = {};
      if (!porJefe[jefe][cat]) porJefe[jefe][cat] = {};
      if (!porJefe[jefe][cat][recu]) porJefe[jefe][cat][recu] = 0;
      porJefe[jefe][cat][recu]++;
    });
    const jefesOrdenados = Object.entries(porJefe).sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, recus) => sum + Object.values(recus).reduce((x, y) => x + y, 0), 0);
      const totalB = Object.values(b[1]).reduce((sum, recus) => sum + Object.values(recus).reduce((x, y) => x + y, 0), 0);
      return totalB - totalA;
    });
    return { jefesOrdenados, reincidencias };
  }, [datosFiltrados]);

  // ─────────────────────────────────────────
  // TABLA 4: Jefe + Ciudad + Reincidencia
  // ─────────────────────────────────────────
  const tabla4 = useMemo(() => {
    const reincidencias = [...new Set(datosFiltrados.map(i => i.recurrencia || "Sin dato"))].sort();
    const porJefe = {};
    datosFiltrados.forEach(item => {
      const jefe   = item.nombre_jefe || "Sin Jefe";
      const ciudad = item.ciudad      || "Sin Ciudad";
      const recu   = item.recurrencia || "Sin dato";
      if (!porJefe[jefe]) porJefe[jefe] = {};
      if (!porJefe[jefe][ciudad]) porJefe[jefe][ciudad] = {};
      if (!porJefe[jefe][ciudad][recu]) porJefe[jefe][ciudad][recu] = 0;
      porJefe[jefe][ciudad][recu]++;
    });
    const jefesOrdenados = Object.entries(porJefe).sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, recus) => sum + Object.values(recus).reduce((x, y) => x + y, 0), 0);
      const totalB = Object.values(b[1]).reduce((sum, recus) => sum + Object.values(recus).reduce((x, y) => x + y, 0), 0);
      return totalB - totalA;
    });
    return { jefesOrdenados, reincidencias };
  }, [datosFiltrados]);

  // ─────────────────────────────────────────
  // DESCARGA CSV — 4 tablas en un solo archivo
  // ─────────────────────────────────────────
  const descargarCSV = () => {
    const sep = ";";
    const nl  = "\n";
    const q   = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    const filas = [];

    const filtrosActivos = [
      filtroMes.length      > 0 ? `Mes: ${filtroMes.join(", ")}`           : null,
      filtroPrioridad.length > 0 ? `Prioridad: ${filtroPrioridad.join(", ")}` : null,
      filtroCausa.length    > 0 ? `Causa Falla: ${filtroCausa.join(", ")}` : null,
    ].filter(Boolean).join(" | ") || "Todos";

    filas.push(q("RESUMEN ICR — PANEL DE ANÁLISIS"));
    filas.push(q(`Filtros aplicados: ${filtrosActivos}`));
    filas.push(q(`Total registros: ${datosFiltrados.length}`));
    filas.push("");

    // ── TABLA 1 ──
    filas.push(q("TABLA 1 — Ofensores por Agrupación y Categoría"));
    filas.push([q("CAT"), q("Agrupación"), q("Cantidad"), q("% Part"), q("Resumen para ICR")].join(sep));
    Object.entries(tabla1.porCat).sort().forEach(([cat, agrs]) => {
      const totalCat = Object.values(agrs).reduce((a, b) => a + b, 0);
      Object.entries(agrs).sort((a, b) => b[1] - a[1]).forEach(([agr, cant]) => {
        const pct = totalCat > 0 ? ((cant / totalCat) * 100).toFixed(1) : "0.0";
        const pctR = Math.round(totalCat > 0 ? (cant / totalCat) * 100 : 0);
        filas.push([q(cat), q(agr), q(cant), q(`${pct}%`), q(`${String(cant).padStart(2,"0")} Nodos por ${agr}............... [${pctR}%]`)].join(sep));
      });
      filas.push([q(`Total ${cat}`), q(""), q(totalCat), q("100%"), q("")].join(sep));
    });
    filas.push([q("Total general"), q(""), q(tabla1.totalGeneral), q("100%"), q("")].join(sep));
    filas.push("");

    // ── TABLA 2 ──
    filas.push(q("TABLA 2 — Ofensores por Categoría y Reincidencia"));
    filas.push([q("CAT"), ...tabla2.reincidencias.map(r => q(r)), q("Total general")].join(sep));
    Object.entries(tabla2.porCat).sort().forEach(([cat, recus]) => {
      const totalFila = Object.values(recus).reduce((a, b) => a + b, 0);
      filas.push([q(cat), ...tabla2.reincidencias.map(r => q(recus[r] || "")), q(totalFila)].join(sep));
    });
    filas.push([q("Total general"), ...tabla2.reincidencias.map(r => q(tabla2.totales[r] || "")), q(tabla2.total)].join(sep));
    filas.push("");

    // ── TABLA 3 ──
    filas.push(q("TABLA 3 — Ofensores por Jefe, Categoría y Reincidencia"));
    filas.push([q("Jefe"), q("CAT"), ...tabla3.reincidencias.map(r => q(r)), q("Total general")].join(sep));
    tabla3.jefesOrdenados.forEach(([jefe, cats]) => {
      const totalJefe = Object.values(cats).reduce((sum, recus) => sum + Object.values(recus).reduce((a, b) => a + b, 0), 0);
      Object.entries(cats).sort().forEach(([cat, recus]) => {
        const totalCat = Object.values(recus).reduce((a, b) => a + b, 0);
        filas.push([q(jefe), q(cat), ...tabla3.reincidencias.map(r => q(recus[r] || "")), q(totalCat)].join(sep));
      });
      filas.push([q(`Total ${jefe}`), q(""), ...tabla3.reincidencias.map(r => {
        const t = Object.values(cats).reduce((sum, recus) => sum + (recus[r] || 0), 0);
        return q(t || "");
      }), q(totalJefe)].join(sep));
    });
    filas.push([q("Total general"), q(""), ...tabla3.reincidencias.map(r => {
      const t = datosFiltrados.filter(d => (d.recurrencia || "Sin dato") === r).length;
      return q(t || "");
    }), q(datosFiltrados.length)].join(sep));
    filas.push("");

    // ── TABLA 4 ──
    filas.push(q("TABLA 4 — Ofensores por Jefe, Ciudad y Reincidencia"));
    filas.push([q("Jefe"), q("Ciudad"), ...tabla4.reincidencias.map(r => q(r)), q("Total general")].join(sep));
    tabla4.jefesOrdenados.forEach(([jefe, ciudades]) => {
      const totalJefe = Object.values(ciudades).reduce((sum, recus) => sum + Object.values(recus).reduce((a, b) => a + b, 0), 0);
      Object.entries(ciudades).sort().forEach(([ciudad, recus]) => {
        const totalCiudad = Object.values(recus).reduce((a, b) => a + b, 0);
        filas.push([q(jefe), q(ciudad), ...tabla4.reincidencias.map(r => q(recus[r] || "")), q(totalCiudad)].join(sep));
      });
      filas.push([q(`Total ${jefe}`), q(""), ...tabla4.reincidencias.map(r => {
        const t = Object.values(ciudades).reduce((sum, recus) => sum + (recus[r] || 0), 0);
        return q(t || "");
      }), q(totalJefe)].join(sep));
    });
    filas.push([q("Total general"), q(""), ...tabla4.reincidencias.map(r => {
      const t = datosFiltrados.filter(d => (d.recurrencia || "Sin dato") === r).length;
      return q(t || "");
    }), q(datosFiltrados.length)].join(sep));

    const csv    = "\uFEFF" + filas.join(nl);
    const blob   = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url    = URL.createObjectURL(blob);
    const link   = document.createElement("a");
    const fecha  = new Date().toISOString().slice(0, 10);
    link.href     = url;
    link.download = `resumen_icr_${fecha}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────
  // ESTILOS
  // ─────────────────────────────────────────
  const thStyle = {
    padding: "8px 10px",
    background: "#1e293b",
    color: "white",
    textAlign: "left",
    fontSize: "12px",
    whiteSpace: "nowrap"
  };
  const tdStyle = { padding: "6px 10px", fontSize: "12px", borderBottom: "1px solid #e5e7eb" };
  const tdTotal = { ...tdStyle, fontWeight: "700", background: "#f1f5f9" };
  const cardStyle = {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px"
  };
  const selectStyle = {
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "4px 6px",
    fontSize: "12px",
    minWidth: "160px",
    background: "#fff"
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>

      {/* ── CABECERA CON BOTÓN DESCARGA ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <h2 style={{ margin: 0 }}>📊 Resumen ICR — Panel de Análisis</h2>
        {soloLectura && <BannerSoloLectura />}
        <button
          onClick={descargarCSV}
          style={{
            background: "#16a34a",
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
          }}
        >
          ⬇️ Descargar Tablas (.csv)
        </button>
      </div>

      {/* FILTROS */}
      <div style={{ ...cardStyle, marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: "#E30613", borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
            <span style={{ fontSize: "11px", color: "#64748b" }}>— Ctrl para seleccionar varios. Sin seleccion = todos.</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#475569", fontWeight: "600" }}>
              {datosFiltrados.length} registros
            </div>
            {(filtroMes.length > 0 || filtroPrioridad.length > 0 || filtroCausa.length > 0 || filtroAliado.length > 0 || filtroCiudad.length > 0 || filtroJefe.length > 0) && (
              <button onClick={() => { setFiltroMes([]); setFiltroPrioridad([]); setFiltroCausa([]); setFiltroAliado([]); setFiltroCiudad([]); setFiltroJefe([]); }}
                style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#E30613", fontWeight: "600", cursor: "pointer" }}>
                ✕ Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: "10px" }}>

          {/* Mes */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Mes</label>
            <select multiple size={Math.min(opcionesMes.length, 5)} value={filtroMes}
              onChange={(e) => setFiltroMes(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroMes.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesMes.map((m, i) => <option key={i} value={m}>{m}</option>)}
            </select>
            {filtroMes.length > 0 && <button onClick={() => setFiltroMes([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Aliado */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Aliado</label>
            <select multiple size={Math.min(opcionesAliado.length, 5)} value={filtroAliado}
              onChange={(e) => setFiltroAliado(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroAliado.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesAliado.map((a, i) => <option key={i} value={a}>{a}</option>)}
            </select>
            {filtroAliado.length > 0 && <button onClick={() => setFiltroAliado([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Ciudad */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Ciudad</label>
            <select multiple size={Math.min(opcionesCiudad.length, 5)} value={filtroCiudad}
              onChange={(e) => setFiltroCiudad(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroCiudad.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesCiudad.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
            {filtroCiudad.length > 0 && <button onClick={() => setFiltroCiudad([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Jefe de Zona */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Jefe de Zona</label>
            <select multiple size={Math.min(opcionesJefe.length, 5)} value={filtroJefe}
              onChange={(e) => setFiltroJefe(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroJefe.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesJefe.map((j, i) => <option key={i} value={j}>{j}</option>)}
            </select>
            {filtroJefe.length > 0 && <button onClick={() => setFiltroJefe([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Prioridad */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Prioridad</label>
            <select multiple size={Math.min(opcionesPrioridad.length, 5)} value={filtroPrioridad}
              onChange={(e) => setFiltroPrioridad(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroPrioridad.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesPrioridad.map((p, i) => <option key={i} value={p}>{p}</option>)}
            </select>
            {filtroPrioridad.length > 0 && <button onClick={() => setFiltroPrioridad([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

          {/* Causa Falla */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", gridColumn: "span 2" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Causa Falla
              {data.some(i => !i.causa_falla || i.causa_falla.trim() === "") && (
                <span style={{ marginLeft: "6px", background: "#fef9c3", border: "1px solid #fde68a", color: "#92400e", borderRadius: "4px", padding: "1px 5px", fontSize: "9px" }}>⚠️ hay sin asignar</span>
              )}
            </label>
            <select multiple size={Math.min(opcionesCausa.length, 5)} value={filtroCausa}
              onChange={(e) => setFiltroCausa(Array.from(e.target.selectedOptions).map(o => o.value))}
              style={{ padding: "8px 10px", borderRadius: "7px", border: filtroCausa.length > 0 ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer" }}>
              {opcionesCausa.map((c, i) => (
                <option key={i} value={c} style={c === VACIO_LABEL ? { color: "#b45309", fontStyle: "italic", background: "#fef9c3" } : {}}>{c}</option>
              ))}
            </select>
            {filtroCausa.length > 0 && <button onClick={() => setFiltroCausa([])} style={{ fontSize: "11px", color: "#E30613", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
          </div>

        </div>
      </div>

      {/* ── TABLA 1: CAT + Agrupación ── */}
      <div style={cardStyle}>
        <h4 style={{ margin: "0 0 16px 0", color: "#111" }}>📋 Tabla 1 — Ofensores por Agrupación y Categoría</h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={thStyle}>CAT</th>
                <th style={thStyle}>Agrupación</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Cantidad</th>
                <th style={{ ...thStyle, textAlign: "center" }}>% Part</th>
                <th style={thStyle}>Resumen para ICR</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(tabla1.porCat).sort().map(([cat, agrs]) => {
                const totalCat = Object.values(agrs).reduce((a, b) => a + b, 0);
                const rows = Object.entries(agrs).sort((a, b) => b[1] - a[1]);
                return rows.map(([agr, cant], idx) => {
                  const pctExacto = totalCat > 0 ? (cant / totalCat) * 100 : 0;
                  const pctMostrar = pctExacto.toFixed(1);
                  const pctRedondeado = Math.round(pctExacto);
                  return (
                    <tr key={`${cat}-${agr}`} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                      {idx === 0 && (
                        <td rowSpan={rows.length} style={{ ...tdStyle, fontWeight: "700", verticalAlign: "top", background: "#f0f9ff", borderRight: "2px solid #bae6fd" }}>
                          {cat}
                        </td>
                      )}
                      <td style={tdStyle}>{agr}</td>
                      <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{cant}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{pctMostrar}%</td>
                      <td style={{ ...tdStyle, color: "#374151" }}>
                        {String(cant).padStart(2, "0")} Nodos por {agr}............... [{pctRedondeado}%]
                      </td>
                    </tr>
                  );
                }).concat(
                  <tr key={`total-${cat}`} style={{ background: "#e2e8f0" }}>
                    <td style={tdTotal} colSpan={2}>Total {cat}</td>
                    <td style={{ ...tdTotal, textAlign: "center" }}>{totalCat}</td>
                    <td style={{ ...tdTotal, textAlign: "center" }}>100%</td>
                    <td style={tdTotal}></td>
                  </tr>
                );
              })}
              <tr style={{ background: "#1e293b" }}>
                <td style={{ ...tdTotal, color: "white", background: "#1e293b" }} colSpan={2}>Total general</td>
                <td style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{tabla1.totalGeneral}</td>
                <td style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>100%</td>
                <td style={{ background: "#1e293b" }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── TABLA 2: CAT + Reincidencia ── */}
      <div style={cardStyle}>
        <h4 style={{ margin: "0 0 16px 0", color: "#111" }}>📋 Tabla 2 — Ofensores por Categoría y Reincidencia</h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={thStyle}>CAT</th>
                {tabla2.reincidencias.map((r, i) => (
                  <th key={i} style={{ ...thStyle, textAlign: "center" }}>{r}</th>
                ))}
                <th style={{ ...thStyle, textAlign: "center" }}>Total general</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(tabla2.porCat).sort().map(([cat, recus], idx) => {
                const totalFila = Object.values(recus).reduce((a, b) => a + b, 0);
                return (
                  <tr key={cat} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                    <td style={{ ...tdStyle, fontWeight: "700" }}>{cat}</td>
                    {tabla2.reincidencias.map((r, i) => (
                      <td key={i} style={{ ...tdStyle, textAlign: "center" }}>{recus[r] || ""}</td>
                    ))}
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "700" }}>{totalFila}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#1e293b" }}>
                <td style={{ ...tdTotal, color: "white", background: "#1e293b" }}>Total general</td>
                {tabla2.reincidencias.map((r, i) => (
                  <td key={i} style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>
                    {tabla2.totales[r] || ""}
                  </td>
                ))}
                <td style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{tabla2.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── TABLA 3: Jefe + CAT + Reincidencia ── */}
      <div style={cardStyle}>
        <h4 style={{ margin: "0 0 16px 0", color: "#111" }}>📋 Tabla 3 — Ofensores por Jefe, Categoría y Reincidencia</h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Jefe</th>
                <th style={thStyle}>CAT</th>
                {tabla3.reincidencias.map((r, i) => (
                  <th key={i} style={{ ...thStyle, textAlign: "center" }}>{r}</th>
                ))}
                <th style={{ ...thStyle, textAlign: "center" }}>Total general</th>
              </tr>
            </thead>
            <tbody>
              {tabla3.jefesOrdenados.map(([jefe, cats]) => {
                const totalJefe = Object.values(cats).reduce((sum, recus) =>
                  sum + Object.values(recus).reduce((a, b) => a + b, 0), 0);
                const catRows = Object.entries(cats).sort();
                return [
                  ...catRows.map(([cat, recus], idx) => {
                    const totalCat = Object.values(recus).reduce((a, b) => a + b, 0);
                    return (
                      <tr key={`${jefe}-${cat}`} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        {idx === 0 && (
                          <td rowSpan={catRows.length + 1} style={{ ...tdStyle, fontWeight: "700", verticalAlign: "top", background: "#fef9c3", borderRight: "2px solid #fde68a" }}>
                            {jefe}
                          </td>
                        )}
                        <td style={tdStyle}>{cat}</td>
                        {tabla3.reincidencias.map((r, i) => (
                          <td key={i} style={{ ...tdStyle, textAlign: "center" }}>{recus[r] || ""}</td>
                        ))}
                        <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{totalCat}</td>
                      </tr>
                    );
                  }),
                  <tr key={`total-${jefe}`} style={{ background: "#fef3c7" }}>
                    <td style={{ ...tdTotal, background: "#fef3c7" }}>Total {jefe}</td>
                    {tabla3.reincidencias.map((r, i) => {
                      const t = Object.values(cats).reduce((sum, recus) => sum + (recus[r] || 0), 0);
                      return <td key={i} style={{ ...tdTotal, textAlign: "center", background: "#fef3c7" }}>{t || ""}</td>;
                    })}
                    <td style={{ ...tdTotal, textAlign: "center", background: "#fef3c7" }}>{totalJefe}</td>
                  </tr>
                ];
              })}
              <tr style={{ background: "#1e293b" }}>
                <td style={{ ...tdTotal, color: "white", background: "#1e293b" }} colSpan={2}>Total general</td>
                {tabla3.reincidencias.map((r, i) => {
                  const t = datosFiltrados.filter(d => (d.recurrencia || "Sin dato") === r).length;
                  return <td key={i} style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{t || ""}</td>;
                })}
                <td style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{datosFiltrados.length}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── TABLA 4: Jefe + Ciudad + Reincidencia ── */}
      <div style={cardStyle}>
        <h4 style={{ margin: "0 0 16px 0", color: "#111" }}>📋 Tabla 4 — Ofensores por Jefe, Ciudad y Reincidencia</h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Jefe</th>
                <th style={thStyle}>Ciudad</th>
                {tabla4.reincidencias.map((r, i) => (
                  <th key={i} style={{ ...thStyle, textAlign: "center" }}>{r}</th>
                ))}
                <th style={{ ...thStyle, textAlign: "center" }}>Total general</th>
              </tr>
            </thead>
            <tbody>
              {tabla4.jefesOrdenados.map(([jefe, ciudades]) => {
                const totalJefe = Object.values(ciudades).reduce((sum, recus) =>
                  sum + Object.values(recus).reduce((a, b) => a + b, 0), 0);
                const ciudadRows = Object.entries(ciudades).sort();
                return [
                  ...ciudadRows.map(([ciudad, recus], idx) => {
                    const totalCiudad = Object.values(recus).reduce((a, b) => a + b, 0);
                    return (
                      <tr key={`${jefe}-${ciudad}`} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        {idx === 0 && (
                          <td rowSpan={ciudadRows.length + 1} style={{ ...tdStyle, fontWeight: "700", verticalAlign: "top", background: "#f0fdf4", borderRight: "2px solid #bbf7d0" }}>
                            {jefe}
                          </td>
                        )}
                        <td style={tdStyle}>{ciudad}</td>
                        {tabla4.reincidencias.map((r, i) => (
                          <td key={i} style={{ ...tdStyle, textAlign: "center" }}>{recus[r] || ""}</td>
                        ))}
                        <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{totalCiudad}</td>
                      </tr>
                    );
                  }),
                  <tr key={`total-${jefe}`} style={{ background: "#dcfce7" }}>
                    <td style={{ ...tdTotal, background: "#dcfce7" }}>Total {jefe}</td>
                    {tabla4.reincidencias.map((r, i) => {
                      const t = Object.values(ciudades).reduce((sum, recus) => sum + (recus[r] || 0), 0);
                      return <td key={i} style={{ ...tdTotal, textAlign: "center", background: "#dcfce7" }}>{t || ""}</td>;
                    })}
                    <td style={{ ...tdTotal, textAlign: "center", background: "#dcfce7" }}>{totalJefe}</td>
                  </tr>
                ];
              })}
              <tr style={{ background: "#1e293b" }}>
                <td style={{ ...tdTotal, color: "white", background: "#1e293b" }} colSpan={2}>Total general</td>
                {tabla4.reincidencias.map((r, i) => {
                  const t = datosFiltrados.filter(d => (d.recurrencia || "Sin dato") === r).length;
                  return <td key={i} style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{t || ""}</td>;
                })}
                <td style={{ ...tdTotal, textAlign: "center", color: "white", background: "#1e293b" }}>{datosFiltrados.length}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default ResumenICR;